import { useCallback, useMemo, useState } from "react";
import { Pressable, ScrollView } from "react-native";
import { Stack, useRouter } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
import { WebLayout } from "@src/components/web/WebLayout.web";
import { RightDrawer } from "@src/components/web/RightDrawer.web";
import { Card, Text, View } from "@src/components";
import { Icon } from "@src/components/Icon";
import { UpcomingExceptions } from "@src/features/alarms/components/UpcomingExceptions";
import { ManageAlarmSettingsCard } from "@src/features/alarms/components/ManageAlarmSettingsCard";
import { ManageOptionsList, type ManageOption } from "@src/features/alarms/components/ManageOptionsList";
import { ManageExceptionsContent } from "@src/features/alarms/components/ManageExceptionsContent";
import { CreateExceptionContent } from "@src/features/alarms/components/CreateExceptionContent";
import { useAggregatedExceptions } from "@src/features/alarms/hooks/useAggregatedExceptions";
import { useSiteCameraKeys } from "@src/features/cameras/hooks/useSiteCameraKeys";
import { groupCamerasBySite } from "@src/features/cameras/utils/camera-group";
import { useSelectedSiteStore } from "@src/stores/selected-site-store";

type DrawerView = "menu" | "exceptions" | "create-exception";

const DRAWER_TITLES: Record<DrawerView, string> = {
  menu: "Manage schedule & alarms",
  exceptions: "Manage exceptions",
  "create-exception": "New exception",
};

export default function TenantAlarmSettingsScreen() {
  const router = useRouter();
  const { theme } = useUnistyles();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerView, setDrawerView] = useState<DrawerView>("menu");
  const { cameras, cameraKeys, selectedSiteId } = useSiteCameraKeys();
  const setSelectedSiteId = useSelectedSiteStore((s) => s.setSelectedSiteId);
  const effectiveSiteId = selectedSiteId ?? "";

  const siteGroups = useMemo(() => {
    if (!cameras) return [];
    return groupCamerasBySite(cameras, "site");
  }, [cameras]);

  // Exceptions data
  const { isLoading: isLoadingExceptions, exceptions: aggregatedExceptions } = useAggregatedExceptions(
    effectiveSiteId,
    cameraKeys,
  );

  const handleManagePress = useCallback(() => {
    setDrawerView("menu");
    setDrawerVisible(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerVisible(false);
    setDrawerView("menu");
  }, []);

  const handleDrawerBack = useCallback(() => {
    setDrawerView("menu");
  }, []);

  const manageOptions: ManageOption[] = useMemo(
    () => [
      {
        id: "routines",
        title: "Routines",
        description: "Manage recurring arming and disarming schedules.",
        icon: "calendarRefresh",
        onPress: () => {
          setDrawerVisible(false);
          router.push("/alarm-settings/manage-routines");
        },
      },
      {
        id: "exceptions",
        title: "Exceptions",
        description: "Schedule temporary overrides for holidays or maintenance.",
        icon: "calendarEvent",
        onPress: () => setDrawerView("exceptions"),
      },
      {
        id: "response",
        title: "Alarm response",
        description: "Configure sirens and strobes.",
        icon: "sirenDown",
        onPress: () => {
          setDrawerVisible(false);
          router.push("/alarm-settings/manage-alarm-response");
        },
      },
      // {
      //   id: "notifications",
      //   title: "Notifications",
      //   description: "Send email or SMS notifications when an alarm is activated.",
      //   icon: "bellOutline",
      //   onPress: () => {},
      // },
    ],
    [router],
  );

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <WebLayout>
        <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ flexGrow: 1 }}>
          <View flex={1} p="7xl">
            <View mb="4xl">
              <Text textStyle="lg/medium">Alarm settings</Text>
            </View>
            {selectedSiteId ?
              <View flexDirection="row" gap="3xl">
                <Card flex={2}>
                  <UpcomingExceptions siteId={effectiveSiteId} cameraKeys={cameraKeys} />
                </Card>
                <View flex={1}>
                  <ManageAlarmSettingsCard onPress={handleManagePress} />
                </View>
              </View>
            : <View gap="3xl">
                <View gap="xxs">
                  <Text textStyle="md/medium">Select a Site</Text>
                  <Text textStyle="sm/regular" color="textMuted">
                    Choose a site below to view and manage its alarm settings.
                  </Text>
                </View>
                <View flexDirection="row" flexWrap="wrap" gap="3xl">
                  {siteGroups.map((group) => (
                    <Pressable key={group.siteId} onPress={() => setSelectedSiteId(group.siteId)}>
                      <Card
                        style={{
                          width: 280,
                          padding: theme.spacing["4xl"],
                          gap: theme.spacing.xs,
                        }}
                      >
                        <View flexDirection="row" alignItems="center" gap="xs">
                          <Icon name="building" size={20} color={theme.colors.textMuted} />
                          <Text textStyle="sm/medium">{group.siteName}</Text>
                        </View>
                        {group.siteSubtitle && (
                          <Text textStyle="xs/regular" color="textMuted">
                            {group.siteSubtitle}
                          </Text>
                        )}
                        <Text textStyle="xs/regular" color="textMuted">
                          {group.cameras.length} {group.cameras.length === 1 ? "camera" : "cameras"}
                        </Text>
                      </Card>
                    </Pressable>
                  ))}
                </View>
              </View>
            }
          </View>
        </ScrollView>
      </WebLayout>
      <RightDrawer visible={drawerVisible} onClose={handleDrawerClose} title={DRAWER_TITLES[drawerView]} width={450}>
        {drawerView === "menu" && <ManageOptionsList options={manageOptions} />}
        {drawerView === "exceptions" && (
          <View>
            <Pressable
              onPress={handleDrawerBack}
              style={{ flexDirection: "row", alignItems: "center", gap: 4, marginBottom: theme.spacing["3xl"] }}
            >
              <Icon name="chevronLeft" size={20} color={theme.colors.text} />
              <Text textStyle="xs/medium" color="text">
                Back
              </Text>
            </Pressable>
            <ManageExceptionsContent
              isLoading={isLoadingExceptions}
              exceptions={aggregatedExceptions}
              onEditException={(group) => {
                setDrawerVisible(false);
                router.push({
                  pathname: "/alarm-settings/edit-exception",
                  params: {
                    exception: JSON.stringify({ reason: group.reason, action: group.action, period: group.period }),
                    includedCameraKeys: JSON.stringify(group.cameraKeys),
                    timezone: group.timezone,
                  },
                });
              }}
              onCreateException={() => setDrawerView("create-exception")}
            />
          </View>
        )}
        {drawerView === "create-exception" && (
          <View>
            <Pressable
              onPress={() => setDrawerView("exceptions")}
              style={{ flexDirection: "row", alignItems: "center", gap: 4, marginBottom: theme.spacing["3xl"] }}
            >
              <Icon name="chevronLeft" size={20} color={theme.colors.text} />
              <Text textStyle="xs/medium" color="text">
                Back
              </Text>
            </Pressable>
            <CreateExceptionContent
              onSelectSuggestion={(exception) => {
                setDrawerVisible(false);
                router.push({
                  pathname: "/alarm-settings/edit-exception",
                  params: {
                    exception: JSON.stringify(exception),
                    includedCameraKeys: JSON.stringify([]),
                    timezone: "UTC",
                    isNew: "true",
                  },
                });
              }}
              onCreateNew={(params) => {
                setDrawerVisible(false);
                router.push({
                  pathname: "/alarm-settings/edit-exception-details",
                  params,
                });
              }}
            />
          </View>
        )}
      </RightDrawer>
    </>
  );
}
